"use client";

import { useEffect, useMemo } from "react";

import { useStudio } from "@/contexts/studio-context";
import { getPianoAuditionSynth } from "@/lib/audio/piano-audition-synth";
import { auditionSeconds, pieceAuditionTrack } from "@/lib/harmony/audition";
import { GRACE_MAX_MS } from "@/lib/harmony/take";
import { MidiAuditionPlayer, SCHEDULE_AHEAD_MS } from "@/lib/midi/midi-audition-player";
import type { AnalysisNote } from "@/lib/music/analyzer-types";

/**
 * De combien chaque segment sonne au-delà de sa fin, en réserve.
 *
 * La grâce laisse la musique continuer après la porte, et une frappe pendant
 * la grâce **prolonge** le segment au lieu de le relancer. Pour que ce soit
 * possible, le son doit déjà avoir été demandé jusque-là : la réserve couvre
 * la plus longue grâce possible, plus l'avance à laquelle le lecteur engage
 * ses notes, pour qu'une prolongation venue au tout dernier moment de la
 * grâce trouve encore un lecteur en marche et une avance entière.
 *
 * C'est une réserve, pas la grâce : celle-ci est mesurée par la prise, qui
 * fait taire le son quand elle s'écoule. Le lecteur ne s'arrête de lui-même
 * que si la prise ne l'a pas fait avant, ce qui est un filet et non la règle.
 */
const RESERVE_MS = GRACE_MAX_MS + SCHEDULE_AHEAD_MS;

/**
 * Le passage d'origine, joué à l'arrivée sur chaque accord.
 *
 * On entend le morceau tel qu'il est écrit sur la durée exacte de l'accord
 * montré, puis on pose la prise dessus, pendant ou juste après. C'est ce
 * rapprochement qui fait comprendre qu'une triade nommée `Bm/D` est bien ce
 * qu'on vient d'entendre, et aucun nom de degré ne le remplace.
 *
 * ## À chaque arrivée, quelle qu'en soit la cause
 *
 * L'extrait se relance dès que l'accord change, qu'on y soit arrivé en prenant
 * le précédent ou en cliquant ailleurs dans la minimap. C'est la même règle
 * pour les deux parce que c'est le même fait : cet accord-ci vient d'arriver
 * sous les mains, et il se présente.
 *
 * ## Il ne s'interrompt pas quand on joue
 *
 * On joue **par-dessus**, c'est le but. Le lecteur n'écoute donc pas le piano,
 * et rien ne coupe l'extrait avant sa fin sauf l'arrivée de l'accord suivant.
 *
 * ## Il continue, ou il repart
 *
 * Un segment qui **continue** le précédent ne relance rien : le lecteur en
 * marche voit seulement son point d'arrêt reculer, et les voix tenues
 * restent tenues. Relancer aurait relâché tout ce qui sonnait et réattaqué
 * depuis la porte, c'est-à-dire produit à chaque porte le hoquet que la
 * grâce vise à supprimer. Un segment qui repart — après une grâce écoulée,
 * après un saut — coupe et relance, comme avant.
 *
 * Si le lecteur s'est arrêté entre-temps, ce qui n'arrive qu'à la course
 * entre sa réserve et la prise, le segment repart de sa porte. Le pire cas
 * est donc l'ancien comportement.
 *
 * ## Le même piano que les mains
 *
 * L'extrait sort du banc de `piano-audition-synth`, c'est-à-dire du Salamander
 * que joue le piano-roll, et non de la banque General MIDI de la fenêtre
 * d'import. Ça n'est pas un détail de confort : tout l'exercice consiste à
 * reconnaître dans la prise qu'on pose ce qu'on vient d'entendre, et deux
 * timbres différents mettent une couture là où il faut une continuité.
 *
 * Conséquence directe sur les niveaux : voir `AUDITION_GAIN` dans
 * `lib/harmony/audition.ts`, qui n'a plus rien à compenser.
 *
 * ## Hors transport
 *
 * Rien ici ne touche à la tête de lecture. Passer par le transport ferait
 * courir le curseur pendant l'extrait, et l'écran, qui suit désormais le
 * transport, se mettrait à chasser sa propre écoute. Le lecteur d'écoute a son
 * horloge, celle du contexte audio, et il est fait exactement pour ça.
 */
export function useChordAudition({
  notes,
  tickToSeconds,
  window: chordWindow,
  onStarted,
}: {
  /** Les notes du morceau **d'origine**, pas celles du niveau. */
  readonly notes: readonly AnalysisNote[];
  readonly tickToSeconds: (tick: number) => number;
  /**
   * La fenêtre à faire entendre, ou `null` pour ne rien jouer.
   *
   * `null` couvre le temps où aucun accord n'est sous les mains.
   */
  readonly window: {
    readonly id: string;
    readonly startTick: number;
    readonly endTick: number;
    /** Il continue le son en cours au lieu de repartir de sa porte. */
    readonly continued: boolean;
  } | null;
  /**
   * Le passage vient de partir, pour de vrai.
   *
   * Pas au moment où on le demande : les échantillons du programme peuvent
   * encore charger, et le premier accord attend alors `whenReady`. Ce qui se
   * cale sur le son — la tête de lecture du rail — doit partir avec lui et non
   * avec l'intention de le jouer, sinon elle prend l'avance du chargement et ne
   * la rend jamais.
   */
  readonly onStarted?: (id: string) => void;
}): void {
  const { studio } = useStudio();

  const player = useMemo<MidiAuditionPlayer | null>(() => {
    if (!studio || notes.length === 0) {
      return null;
    }

    const ctx = studio.getAudioContext();

    return new MidiAuditionPlayer(ctx, getPianoAuditionSynth(ctx), [
      pieceAuditionTrack(notes, tickToSeconds),
    ]);
  }, [studio, notes, tickToSeconds]);

  // Au démontage, et pas au montage : `dispose` coupe les minuteries du
  // lecteur, et l'appeler tout de suite aurait désarmé celui qu'on vient de
  // construire.
  useEffect(
    () => () => {
      player?.dispose();
    },
    [player],
  );

  const id = chordWindow?.id ?? null;
  const startTick = chordWindow?.startTick ?? 0;
  const endTick = chordWindow?.endTick ?? 0;
  const continued = chordWindow?.continued ?? false;

  useEffect(() => {
    if (player === null) {
      return;
    }

    // Plus rien à faire entendre : après une grâce écoulée, ou sans accord
    // sous les mains. C'est ici que le son se tait, et non au nettoyage de
    // l'effet, qui coupait avant même de savoir si le segment d'après
    // continuait.
    if (id === null) {
      player.stop();

      return;
    }

    let cancelled = false;
    const startSec = auditionSeconds(tickToSeconds, startTick);
    const endSec = auditionSeconds(tickToSeconds, endTick);
    const durationSec = Math.max(endSec - startSec, 0);

    if (durationSec <= 0) {
      return;
    }

    const reserveSec = RESERVE_MS / 1000;

    if (continued && player.extend(endSec + reserveSec)) {
      onStarted?.(id);

      return;
    }

    // Les échantillons du programme peuvent encore charger au premier accord :
    // jouer avant les aurait perdus en silence. `playAll` coupe de lui-même ce
    // qui sonnait, donc l'accord précédent s'arrête net à l'arrivée du suivant.
    void player.whenReady().then(() => {
      if (!cancelled) {
        player.playAll(startSec, durationSec + reserveSec, { openEnded: true });
        onStarted?.(id);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [player, id, startTick, endTick, continued, tickToSeconds, onStarted]);
}
